import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.store';
import { useMounted } from '../hooks/useMounted';
import {
  DEFAULT_UI_LOCALE,
  EXPLORE_LANG_STORAGE_KEY,
  isIso2,
  isSupportedUILocale,
  withLocale,
} from '../locale';

interface LayoutProps {
  children: React.ReactNode;
  showHeader?: boolean;
  title?: string;
  wide?: boolean;
}

export const Layout: React.FC<LayoutProps> = ({ children, showHeader = true, title, wide = false }) => {
  const mounted = useMounted();
  const isAuthenticatedRaw = useAuthStore((state) => state.isAuthenticated);
  const userRaw = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  // Avoid hydration mismatch: pretend signed-out until client has mounted and
  // read localStorage. The header re-renders post-mount with the real value.
  const isAuthenticated = mounted && isAuthenticatedRaw;
  const user = mounted ? userRaw : null;
  const location = useLocation();

  // First path segment is the UI locale when it matches a supported code.
  const currentLocale = useMemo(() => {
    const first = location.pathname.split('/')[1];
    return isSupportedUILocale(first) ? first : DEFAULT_UI_LOCALE;
  }, [location.pathname]);

  const onLoginPage = location.pathname.endsWith('/login');

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const containerClass = wide ? 'max-w-6xl' : 'max-w-lg';
  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `text-sm font-medium transition ${isActive ? 'text-primary-700' : 'text-gray-500 hover:text-gray-800'}`;

  const initial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();

  // Compute the Explore link target with the user's preferred content
  // language so clicks don't bounce through the redirect logic. Stick to
  // the locale-only default during SSR/first hydration so server and
  // client agree; the post-mount re-render swaps in the user's pick.
  const exploreHref = useMemo(() => {
    const ui = currentLocale;
    if (!mounted) return `/${ui}/explore`;
    try {
      const stored = typeof localStorage !== 'undefined'
        ? localStorage.getItem(EXPLORE_LANG_STORAGE_KEY)
        : null;
      if (isIso2(stored)) return `/${ui}/explore/${stored}`;
    } catch {
      // fall through
    }
    const nav = typeof navigator !== 'undefined' && navigator.language
      ? navigator.language.toLowerCase().slice(0, 2)
      : 'en';
    return isIso2(nav) ? `/${ui}/explore/${nav}` : `/${ui}/explore/en`;
  }, [currentLocale, mounted]);

  const homeHref = withLocale(currentLocale, '/');
  const libraryHref = withLocale(currentLocale, '/library');
  const loginHref = withLocale(currentLocale, '/login');

  return (
    <div className="min-h-screen bg-gray-50">
      {showHeader && (
        <header className="bg-white border-b border-gray-200 px-4 py-3">
          <div className={`${containerClass} mx-auto flex items-center justify-between gap-3`}>
            <div className="flex items-center gap-5 min-w-0">
              <Link to={homeHref} className="text-xl font-bold text-primary-600 hover:text-primary-700 transition whitespace-nowrap">
                {title || '📖 StoryChain'}
              </Link>
              {!onLoginPage && (
                <nav className="hidden sm:flex items-center gap-4">
                  <NavLink to={exploreHref} className={navLinkClass}>Explore</NavLink>
                  {isAuthenticated && (
                    <NavLink to={libraryHref} className={navLinkClass}>My Library</NavLink>
                  )}
                </nav>
              )}
            </div>
            {!onLoginPage && (
              isAuthenticated ? (
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setMenuOpen((o) => !o)}
                    className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 font-semibold flex items-center justify-center hover:bg-primary-200 transition"
                    title={user?.name || user?.email || 'Account'}
                  >
                    {initial}
                  </button>
                  {menuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm text-gray-900 truncate">{user?.name || 'Account'}</p>
                        {user?.email && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
                      </div>
                      <button
                        onClick={() => { setMenuOpen(false); logout(); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={loginHref}
                  className="text-sm font-medium bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition whitespace-nowrap"
                >
                  Sign in
                </Link>
              )
            )}
          </div>
          {!onLoginPage && (
            <nav className="sm:hidden mt-2 flex items-center gap-4 max-w-lg mx-auto">
              <NavLink to={exploreHref} className={navLinkClass}>Explore</NavLink>
              {isAuthenticated && (
                <NavLink to={libraryHref} className={navLinkClass}>My Library</NavLink>
              )}
            </nav>
          )}
        </header>
      )}
      <main className={`${containerClass} mx-auto px-4 py-6`}>
        {children}
      </main>
    </div>
  );
};

